import React from 'react';
import { FundOwnershipCapTableDetailsModel, FundOwnershipDetailModel, SecurityModel } from 'api';
import securityTypes from 'common/constants';
import { Row, Rows } from 'common/types/scalarSpreadsheet';
import { UseStoreValue } from 'common/types/store';
import { ValueWithLinkViewer } from 'components';
import {
  ALLOCATION_WEIGHTED_SHARE_VALUES_SPREADSHEET_COLUMN_KEY_PREFIX,
  ALLOCATION_WEIGHTED_SHARE_VALUES_SPREADSHEET_FUND_SEPARATOR_KEY,
  ALLOCATION_WEIGHTED_SHARE_VALUES_SPREADSHEET_SECURITY_TOTAL_KEY,
  ALLOCATION_WEIGHTED_SHARE_VALUES_SPREADSHEET_TABLE,
  SHEET_ALIASES_CONSTANTS,
} from 'pages/ValuationsAllocation/common/constants/weightedShareValues';
import { generateColumnKey, getObjectValue, getSlugValue, getStringValue, Logger } from 'utilities';
import { RelatedEntity } from '../../../../types';

const { ALLOCATION_WEIGHTED_SHARE_VALUES_SPREADSHEET_HEADER_TITLE } = SHEET_ALIASES_CONSTANTS;

type SecurityMapping = {
  entityType: 'fund' | 'relatedEntity';
  fundOwnershipDetails: FundOwnershipDetailModel[];
};

/**
 * Generates a mapping of fund ownership details to their respective entities (funds or related entities)
 * @param foDetails - Array of fund ownership details
 * @param securities - Array of security models
 * @returns Map of entity ID to security mapping
 */
const generateSecurityMapping = (
  foDetails: FundOwnershipDetailModel[],
  securities: SecurityModel[]
): Map<number, SecurityMapping> => {
  const securityMapping = foDetails.reduce((acc, curr) => {
    const { fund, related_entity } = curr;

    // common should not be on the list when fund and related_entity are defined

    const securityBelongsToPrimaryCompany = !!securities.find(s => s.id === curr.security);

    if (!securityBelongsToPrimaryCompany && !curr.custom_security_name) {
      Logger.debug('Security skipped - belongs to different company', curr);
      return acc;
    }

    if (fund) {
      const fundExistsInAcc = acc.get(fund);
      if (!fundExistsInAcc) {
        return acc.set(fund, { entityType: 'fund', fundOwnershipDetails: [curr] });
      }

      acc.set(fund, { entityType: 'fund', fundOwnershipDetails: [...fundExistsInAcc.fundOwnershipDetails, curr] });
    }

    if (related_entity) {
      const relatedEntityExistsInAcc = acc.get(related_entity);
      if (!relatedEntityExistsInAcc) {
        return acc.set(related_entity, { entityType: 'relatedEntity', fundOwnershipDetails: [curr] });
      }

      acc.set(related_entity, {
        entityType: 'relatedEntity',
        fundOwnershipDetails: [...relatedEntityExistsInAcc.fundOwnershipDetails, curr],
      });
    }

    return acc;
  }, new Map<number, SecurityMapping>());

  return securityMapping;
};

const getUniqueFoDetails = (foDetails: FundOwnershipDetailModel[]): FundOwnershipDetailModel[] => {
  const uniqueMap = new Map<number, FundOwnershipDetailModel>();

  foDetails.forEach(detail => {
    const existing = uniqueMap.get(detail.security ?? 0);
    if (!existing) {
      uniqueMap.set(detail.security ?? 0, detail);
    }
  });

  // Convert the Map back to an array
  return Array.from(uniqueMap.values());
};

const generateFundOwnershipDetailRows = (
  foDetails: FundOwnershipDetailModel[],
  funds: UseStoreValue['fundList'],
  securities: SecurityModel[],
  childCompanies: RelatedEntity[]
): Rows => {
  const securityMapping = generateSecurityMapping(foDetails, securities);

  const rows: Rows = [];

  securityMapping.forEach((fods, entityId) => {
    const { entityType, fundOwnershipDetails } = fods;

    const uniqueFoDetails = getUniqueFoDetails(fundOwnershipDetails);

    const entityName =
      entityType === 'fund'
        ? getStringValue(funds?.find(f => f.id === entityId)?.name ?? '')
        : getStringValue(childCompanies?.find(c => +c.company_id === entityId)?.company_name ?? '');

    rows.push({
      alias: ALLOCATION_WEIGHTED_SHARE_VALUES_SPREADSHEET_FUND_SEPARATOR_KEY,
      className: `${ALLOCATION_WEIGHTED_SHARE_VALUES_SPREADSHEET_TABLE}-fund-separator subheader-division`,
      gridType: 'string',
      hidden: true,
      readOnly: true,
      ignoreRowCopy: true,
      value: <ValueWithLinkViewer isDarkMode name={entityName} />,
      data: {
        isRelatedEntityInvestmentInCompany: entityType === 'relatedEntity',
      },
    } as Row);

    uniqueFoDetails.forEach(foDetail => {
      rows.push({
        alias: generateColumnKey({
          // validate these carry over here
          id: `${foDetail.security ?? foDetail.custom_security_name}_${foDetail.fund ?? foDetail.related_entity}`,
          name: getStringValue(foDetail.security_name ?? foDetail.custom_security_name),
          prefix: ALLOCATION_WEIGHTED_SHARE_VALUES_SPREADSHEET_COLUMN_KEY_PREFIX,
        }),
        className: 'subtitle-indented',
        data: {
          isRelatedEntityInvestmentInCompany: entityType === 'relatedEntity',
        },
        gridType: 'string',
        ignoreRowCopy: true,
        isExpr: foDetail.security_type !== securityTypes.CONVERTIBLE_NOTES,
        value: getStringValue(foDetail.security_name ?? foDetail.custom_security_name),
      } as Row);
    });

    // Total
    rows.push({
      alias: ALLOCATION_WEIGHTED_SHARE_VALUES_SPREADSHEET_SECURITY_TOTAL_KEY,
      className: `${ALLOCATION_WEIGHTED_SHARE_VALUES_SPREADSHEET_TABLE}-security-total subtitle`,
      gridType: 'string',
      customKey: `TOTAL_${entityId}`,
      readOnly: true,
      ignoreRowCopy: true,
      value: `${entityName} Total`,
      data: {
        isRelatedEntityInvestmentInCompany: entityType === 'relatedEntity',
      },
    } as Row);
  });

  return rows;
};

const customRowConfig = (
  currentCompanyName: string,
  fundOwnership: FundOwnershipCapTableDetailsModel,
  funds: UseStoreValue['fundList'],
  securities: SecurityModel[],
  childCompanies: RelatedEntity[]
): Rows => {
  const { fund_ownership_detail: fods } = getObjectValue(fundOwnership);
  const uniqueFoDetails = getUniqueFoDetails(fods);
  const allAreCustomSecurityOwnerships = Boolean(uniqueFoDetails.every(foDetail => foDetail.security === null));
  if (uniqueFoDetails.length === 0 || allAreCustomSecurityOwnerships) {
    return [];
  }

  return [
    {
      alias: ALLOCATION_WEIGHTED_SHARE_VALUES_SPREADSHEET_HEADER_TITLE,
      className: `${ALLOCATION_WEIGHTED_SHARE_VALUES_SPREADSHEET_TABLE}-${getSlugValue(
        ALLOCATION_WEIGHTED_SHARE_VALUES_SPREADSHEET_HEADER_TITLE
      )} division-bottom-only`,
      gridType: 'string',
      ignoreRowCopy: true,
      largeHeader: true,
      readOnly: true,
      value: currentCompanyName,
    } as Row,
    ...generateFundOwnershipDetailRows(fods, funds, securities, childCompanies),
  ];
};

export default customRowConfig;
